import {
  Box,
  Dialog,
  IconButton,
  Tab,
  Tabs,
  Typography,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import TabContainer from "./TabContainer";

const RED = "#CC0000";
const HERO_GRADIENT = "linear-gradient(135deg, rgba(15,23,42,0.0) 0%, rgba(15,23,42,0.35) 55%, rgba(15,23,42,0.85) 100%)";
const DAY_FULL = { MON: "Monday", TUE: "Tuesday", WED: "Wednesday", THU: "Thursday", FRI: "Friday", SAT: "Saturday", SUN: "Sunday" };

// Format a [hour, minute] pair as 12-hour time, e.g. [9, 0] → "9:00 AM",
// [23, 59] → "11:59 PM", [0, 0] → "12:00 AM".
const fmt12 = (h, m) => {
  const hour = Number(h);
  const min = Number(m);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(min).padStart(2, "0")} ${period}`;
};


// A truck's week, grouped by day. Two stops on the same day (lunch downtown,
// evening at the brewery) both belong under that day — which is the whole
// difference between a truck's schedule and a restaurant's opening hours.
const groupStopsByDay = (stops) => {
  const byDay = new Map();
  for (const stop of stops || []) {
    const key = stop.dayOfWeek || "";
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(stop);
  }
  return byDay;
};

function a11yProps(index) {
  return {
    id: `restaurant-info-tab-${index}`,
    "aria-controls": `restaurant-info-tabpanel-${index}`,
  };
}

function RestaurantInfo({ isVisible, toggleModal, restaurantInfo }) {
  const theme = useTheme();
  const extraSmall = useMediaQuery(theme.breakpoints.down("md"));
  const [tabValue, setTabValue] = useState(0);
  const { t } = useTranslation();

  const handleChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  if (!restaurantInfo) return null;

  const ratings = restaurantInfo.reviewData?.ratings ?? 0;
  const totalReviews = restaurantInfo.reviewData?.total ?? 0;
  const reviews = restaurantInfo.reviewData?.reviews ?? [];
  const deals = restaurantInfo.deals ?? [];
  const openingTimes = restaurantInfo.openingTimes ?? [];

  // A food truck has a route, not opening hours: each day lists the places it
  // parks and the window it is at each one.
  const isTruck = restaurantInfo.isMobile === true;
  const isOpenNow = restaurantInfo.isOpenNow === true;
  const truckDays = isTruck
    ? Array.from(groupStopsByDay(restaurantInfo.upcomingStops).entries())
    : [];
  const truckHeadline = isTruck
    ? (isOpenNow
        ? (restaurantInfo.serviceLocation?.label || null)
        : (restaurantInfo.serviceLocation?.nextStopLabel || null))
    : null;

  return (
    <Dialog
      fullScreen={extraSmall}
      onClose={toggleModal}
      open={isVisible}
      scroll="body"
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: extraSmall ? 0 : "22px",
          overflow: "hidden",
          bgcolor: "#fff",
        },
      }}
    >
      {/* ─── Hero image with overlay ─────────────────────────────────── */}
      <Box sx={{ position: "relative", height: { xs: 220, md: 280 }, overflow: "hidden" }}>
        <Box
          sx={{
            position: "absolute", inset: 0,
            backgroundImage: `url('${restaurantInfo?.image ?? ""}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Box sx={{ position: "absolute", inset: 0, background: HERO_GRADIENT }} />

        {/* Close button */}
        <IconButton
          onClick={toggleModal}
          sx={{
            position: "absolute", top: 12, right: 12,
            bgcolor: "rgba(255,255,255,0.95)",
            "&:hover": { bgcolor: "#fff" },
            boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
          }}
        >
          <CloseRoundedIcon sx={{ color: "#0f172a" }} />
        </IconButton>

        {/* Title block over the image */}
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, p: { xs: 2.5, md: 3 }, color: "#fff" }}>
          <Stack direction="row" alignItems="center" spacing={1.25} mb={0.75} flexWrap="wrap" useFlexGap>
            <Box sx={{
              width: 36, height: 36, borderRadius: "10px",
              bgcolor: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <RestaurantRoundedIcon sx={{ fontSize: 20, color: "#fff" }} />
            </Box>
            <Typography sx={{ fontSize: { xs: "1.4rem", md: "1.8rem" }, fontWeight: 800, lineHeight: 1.15 }}>
              {restaurantInfo.name}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            {/* Rating chip */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.4,
              px: 1, py: 0.4, borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.95)", color: "#0f172a",
              fontSize: "0.8rem", fontWeight: 700,
            }}>
              <StarRoundedIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
              {ratings}<Box component="span" sx={{ opacity: 0.55, fontWeight: 500 }}>/5</Box>
              <Box component="span" sx={{ opacity: 0.55, fontWeight: 500, ml: 0.5 }}>({totalReviews})</Box>
            </Box>

            {/* Preparation time chip (falls back to delivery time if prep not set) */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.5,
              px: 1, py: 0.4, borderRadius: 999,
              bgcolor: "rgba(204,0,0,0.95)", color: "#fff",
              fontSize: "0.78rem", fontWeight: 700,
            }}>
              <AccessTimeRoundedIcon sx={{ fontSize: 14 }} />
              {`Prep ${restaurantInfo.preparationTime ?? restaurantInfo.deliveryTime} ${t("minute")}`}
            </Box>

            {/* Food truck marker — the one thing that explains why the address
                on this page is different from last week's. */}
            {isTruck && (
              <Box sx={{
                display: "inline-flex", alignItems: "center", gap: 0.5,
                px: 1, py: 0.4, borderRadius: 999,
                bgcolor: "rgba(22,101,52,0.95)", color: "#fff",
                fontSize: "0.78rem", fontWeight: 700,
              }}>
                🚚 Food truck
              </Box>
            )}
          </Stack>
        </Box>
      </Box>

      {/* ─── Tabs ────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: "1px solid #e5e7eb", bgcolor: "#fff", px: { xs: 1, md: 3 } }}>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          TabIndicatorProps={{ style: { background: RED, height: 3, borderRadius: 3 } }}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.92rem",
              minHeight: 52,
              color: "#64748b",
            },
            "& .Mui-selected": { color: "#0f172a !important" },
          }}
        >
          <Tab label={t("about")} {...a11yProps(0)} />
          <Tab label={`${t("reviews")} (${totalReviews})`} {...a11yProps(1)} />
        </Tabs>
      </Box>

      {/* ─── Tab content ─────────────────────────────────────────────── */}
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
        {/* ─ About tab ─ */}
        <TabContainer value={tabValue} index={0}>
          {/* Delivery hours card */}
          <Box sx={{
            bgcolor: "#fff", borderRadius: "18px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
            p: { xs: 2, md: 2.5 },
            mb: 2,
          }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "10px",
                bgcolor: "#fef2f2", color: RED,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <AccessTimeRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography fontWeight={800} fontSize="1rem" color="#0f172a">
                {isTruck ? "Where to find us" : t("deliveryHours")}
              </Typography>
            </Stack>

            {isTruck ? (
              <Stack divider={<Divider sx={{ borderColor: "#f1f5f9" }} />}>
                {truckDays.length === 0 ? (
                  <Typography fontSize="0.88rem" color="text.secondary" sx={{ py: 1.5 }}>
                    No stops scheduled this week.
                  </Typography>
                ) : (
                  truckDays.map(([day, stops]) => (
                    <Box key={day} sx={{ py: 1.25 }}>
                      <Stack direction="row" alignItems="center" spacing={1.25} mb={0.75}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#16a34a" }} />
                        <Typography fontSize="0.88rem" fontWeight={700} color="#0f172a">
                          {DAY_FULL[day] || day}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.75} sx={{ pl: 2.5 }}>
                        {stops.map((stop, i) => (
                          <Stack
                            key={`${stop._id}_${i}`}
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            spacing={0.25}
                          >
                            <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                              <Typography fontSize="0.85rem" fontWeight={600} color="#0f172a">
                                {stop.label || stop.address}
                              </Typography>
                              {!!stop.label && (
                                <Typography fontSize="0.75rem" color="text.secondary">
                                  {stop.address}
                                </Typography>
                              )}
                            </Box>
                            <Typography
                              fontSize="0.85rem"
                              fontWeight={600}
                              color="#0f172a"
                              sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                            >
                              {stop.windowLabel}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  ))
                )}
              </Stack>
            ) : (
            <Stack divider={<Divider sx={{ borderColor: "#f1f5f9" }} />}>
              {openingTimes.map((dayOb, index) => {
                const closed = !dayOb.times || dayOb.times.length < 1;
                return (
                  <Stack
                    key={`${dayOb.day}_${index}`}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ py: 1.1 }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: "50%",
                        bgcolor: closed ? "#cbd5e1" : "#16a34a",
                      }} />
                      <Box>
                        <Typography fontSize="0.88rem" fontWeight={700} color="#0f172a">
                          {DAY_FULL[dayOb.day] || dayOb.day}
                        </Typography>
                        <Typography fontSize="0.7rem" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                          {dayOb.day}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ textAlign: "right" }}>
                      {closed ? (
                        <Chip
                          label={t("closedAllDay")}
                          size="small"
                          sx={{
                            fontSize: "0.7rem", fontWeight: 700,
                            bgcolor: "#f1f5f9", color: "#64748b",
                            border: "1px solid #e5e7eb",
                          }}
                        />
                      ) : (
                        <Stack spacing={0.25} alignItems="flex-end">
                          {dayOb.times.map((timeObj, i) => (
                            <Typography
                              key={`TIME_${i}`}
                              fontSize="0.85rem"
                              fontWeight={600}
                              color="#0f172a"
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {fmt12(timeObj.startTime[0], timeObj.startTime[1])}
                              {" – "}
                              {fmt12(timeObj.endTime[0], timeObj.endTime[1])}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
            )}
          </Box>

          {/* Address card */}
          <Box sx={{
            bgcolor: "#fff", borderRadius: "18px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
            p: { xs: 2, md: 2.5 },
          }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "10px",
                bgcolor: "#eff6ff", color: "#1d4ed8",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <LocationOnRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={800} fontSize="1rem" color="#0f172a" mb={0.5}>
                  {isTruck ? (isOpenNow ? "Parked right now" : "Next stop") : t("address")}
                </Typography>
                {isTruck && !!truckHeadline && (
                  <Typography fontSize="0.88rem" fontWeight={700} color="#0f172a">
                    {truckHeadline}
                  </Typography>
                )}
                <Typography fontSize="0.88rem" color="#334155">
                  {(isTruck && restaurantInfo.serviceLocation?.address) || restaurantInfo.address || "—"}
                </Typography>
              </Box>
            </Stack>
          </Box>

        </TabContainer>

        {/* ─ Reviews tab ─ */}
        <TabContainer value={tabValue} index={1}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "12px",
              bgcolor: "#fffbeb", color: "#d97706",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <RateReviewRoundedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography fontWeight={800} fontSize="1.05rem" color="#0f172a">
                {`${totalReviews} ${t("reviews")}`}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <StarRoundedIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                <Typography fontSize="0.85rem" fontWeight={600} color="#334155">
                  {ratings}<Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>/5 average</Box>
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {reviews.length === 0 && (
            <Box sx={{
              p: 4, textAlign: "center", borderRadius: "16px",
              border: "1px dashed #cbd5e1", bgcolor: "#fff",
            }}>
              <Typography color="text.secondary" fontSize="0.9rem" fontWeight={600}>
                No reviews yet.
              </Typography>
            </Box>
          )}

          <Stack spacing={1.5}>
            {reviews.map((review) => {
              // Mask the reviewer name for privacy: "First L." (or just the
              // single name if there's no surname; "Anonymous" when missing).
              const rawName = review.user?.name?.trim();
              let name = "Anonymous";
              if (rawName) {
                const parts = rawName.split(/\s+/);
                name = parts.length > 1
                  ? `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`
                  : parts[0];
              }
              const initial = (name[0] || "?").toUpperCase();
              // createdAt arrives as an epoch-ms string (e.g. "1775065078000"),
              // which new Date(string) can't parse — coerce numeric strings to a
              // number first; otherwise treat it as an ISO date string.
              const rawDate = review.createdAt;
              const parsedDate = rawDate
                ? new Date(/^\d+$/.test(String(rawDate)) ? Number(rawDate) : rawDate)
                : null;
              const reviewDate =
                parsedDate && !isNaN(parsedDate.getTime())
                  ? parsedDate.toLocaleDateString("en-US", {
                      year: "numeric", month: "short", day: "numeric",
                    })
                  : null;
              return (
                <Box
                  key={`REVIEW_${review._id}`}
                  sx={{
                    p: 2, borderRadius: "16px", bgcolor: "#fff",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: "50%",
                        bgcolor: "#0f172a", color: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontWeight: 700, fontSize: "0.9rem",
                      }}>
                        {initial}
                      </Box>
                      <Stack spacing={0}>
                        <Typography fontWeight={700} fontSize="0.92rem" color="#0f172a" lineHeight={1.25}>
                          {name}
                        </Typography>
                        {reviewDate && (
                          <Typography fontSize="0.72rem" color="#94a3b8">
                            {reviewDate}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                    <Box sx={{
                      display: "inline-flex", alignItems: "center", gap: 0.3,
                      px: 0.9, py: 0.3, borderRadius: 999,
                      bgcolor: "#fffbeb", border: "1px solid #fde68a",
                      color: "#92400e", fontWeight: 700, fontSize: "0.8rem",
                    }}>
                      <StarRoundedIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                      {review.rating}
                      <Box component="span" sx={{ opacity: 0.6, fontWeight: 500 }}>/5</Box>
                    </Box>
                  </Stack>
                  {review.description && (
                    <Typography fontSize="0.88rem" color="#475569" sx={{ lineHeight: 1.55 }}>
                      {review.description}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </TabContainer>
      </Box>
    </Dialog>
  );
}

export default React.memo(RestaurantInfo);
